import { useCallback, useEffect, useRef, useState } from 'react';

export type DictationStatus = 'idle' | 'starting' | 'listening' | 'stopping';

type AutoStop = { transcript: string; reason: 'limit' | 'time' | 'error' };

type VoiceDictationOptions = {
	/** Characters the transcript may take; reaching it ends the dictation. */
	maxLength: number;
	timeLimitMs: number;
	/** The dictation ended on its own, keeping its text. */
	onAutoStop: (autoStop: AutoStop) => void;
	/** The dictation failed before anything was recognised; nothing is kept. */
	onStartError: () => void;
};

export const DICTATION_TIME_LIMIT_MS = 120_000;

// Between `stop()` and `end` the recogniser flushes the utterance in flight as a final
// result; in Chrome that is a network round trip, so 1 s is tight.
const END_FALLBACK_MS = 2000;
// A recogniser that keeps ending right after it starts without hearing anything (a tab
// without audio, a device taken away) is failing, not pausing.
const RAPID_END_MS = 1000;
const MAX_RAPID_ENDS = 3;

type Session = {
	recognition: SpeechRecognition;
	segments: string[];
	hasStarted: boolean;
	isStopping: boolean;
	isSettled: boolean;
	stopReason: AutoStop['reason'] | 'start-error' | null;
	resolveConfirm: ((transcript: string) => void) | null;
	fallbackTimer: number | undefined;
	timeLimitTimer: number | undefined;
	startedAt: number;
	finalsSinceStart: number;
	rapidEnds: number;
};

const getRecognitionConstructor = () => window.SpeechRecognition ?? window.webkitSpeechRecognition;

const startRecognition = (session: Session) => {
	session.startedAt = Date.now();
	session.finalsSinceStart = 0;
	try {
		session.recognition.start();
		return true;
	} catch {
		return false;
	}
};

/**
 * Dictation through the browser's Web Speech API, in German.
 *
 * The session lives in refs, not state: the recogniser fires `error` and `end` in the same task,
 * so a state check in the `end` handler would still read the session as healthy and restart it
 * after a failure. React state only mirrors the status for rendering.
 *
 * Every way a dictation ends goes through `finish`, which runs once per session: nothing is
 * delivered twice, and an event that arrives after it is dropped.
 */
const useVoiceDictation = (options: VoiceDictationOptions) => {
	const [status, setStatus] = useState<DictationStatus>('idle');
	const sessionRef = useRef<Session | null>(null);
	const optionsRef = useRef(options);

	useEffect(() => {
		optionsRef.current = options;
	});

	const finish = useCallback(
		(
			session: Session,
			{ discard = false, abort = false }: { discard?: boolean; abort?: boolean } = {}
		) => {
			if (session.isSettled) {
				return;
			}
			session.isSettled = true;
			window.clearTimeout(session.fallbackTimer);
			window.clearTimeout(session.timeLimitTimer);
			const { recognition } = session;
			recognition.onstart = null;
			recognition.onresult = null;
			recognition.onerror = null;
			recognition.onend = null;
			if (abort) {
				recognition.abort();
			}
			if (sessionRef.current === session) {
				sessionRef.current = null;
				setStatus('idle');
			}

			const transcript = discard
				? ''
				: session.segments.join(' ').slice(0, Math.max(0, optionsRef.current.maxLength));
			session.resolveConfirm?.(transcript);
			if (discard || session.stopReason === null) {
				return;
			}
			if (session.stopReason === 'start-error') {
				optionsRef.current.onStartError();
			} else {
				optionsRef.current.onAutoStop({ transcript, reason: session.stopReason });
			}
		},
		[]
	);

	// Waiting for `end` keeps the words that were still being recognised when the user stopped.
	const stopAndWait = useCallback(
		(session: Session) => {
			session.isStopping = true;
			setStatus('stopping');
			session.recognition.stop();
			session.fallbackTimer = window.setTimeout(
				() => finish(session, { abort: true }),
				END_FALLBACK_MS
			);
		},
		[finish]
	);

	const start = useCallback(() => {
		const Recognition = getRecognitionConstructor();
		if (sessionRef.current || !Recognition) {
			return;
		}
		// Otherwise the recogniser transcribes Muki reading an answer aloud.
		if ('speechSynthesis' in window) {
			window.speechSynthesis.cancel();
		}

		const recognition = new Recognition();
		recognition.lang = 'de-DE';
		recognition.continuous = true;
		recognition.interimResults = false;
		recognition.maxAlternatives = 1;

		const session: Session = {
			recognition,
			segments: [],
			hasStarted: false,
			isStopping: false,
			isSettled: false,
			stopReason: null,
			resolveConfirm: null,
			fallbackTimer: undefined,
			timeLimitTimer: undefined,
			startedAt: 0,
			finalsSinceStart: 0,
			rapidEnds: 0,
		};
		sessionRef.current = session;

		recognition.onstart = () => {
			if (!session.hasStarted && !session.isStopping) {
				session.hasStarted = true;
				setStatus('listening');
				// Armed here rather than in start(): the permission prompt may have been open until now.
				session.timeLimitTimer = window.setTimeout(() => {
					if (!session.isStopping) {
						session.stopReason = 'time';
						stopAndWait(session);
					}
				}, optionsRef.current.timeLimitMs);
			}
		};

		recognition.onresult = (event) => {
			// In continuous mode the list holds every result of the session; only the entries
			// from `resultIndex` on are new.
			for (let index = event.resultIndex; index < event.results.length; index++) {
				const result = event.results[index];
				const text = result.isFinal ? result[0].transcript.trim() : '';
				if (text) {
					session.segments.push(text);
					session.finalsSinceStart += 1;
				}
			}
			// The text is already in hand, so the limit ends the dictation without waiting for `end`.
			if (session.segments.join(' ').length >= optionsRef.current.maxLength) {
				if (!session.isStopping) {
					session.stopReason = 'limit';
				}
				finish(session);
				recognition.stop();
			}
		};

		recognition.onerror = (event) => {
			// `aborted` follows our own `abort()`; after `no-speech` the browser ends and the
			// `end` handler restarts.
			if (event.error === 'aborted' || event.error === 'no-speech' || session.isStopping) {
				return;
			}
			if (session.segments.length === 0) {
				session.stopReason = 'start-error';
				finish(session, { abort: true });
				return;
			}
			session.stopReason = 'error';
			stopAndWait(session);
		};

		recognition.onend = () => {
			if (session.isStopping) {
				finish(session);
				return;
			}
			const isRapidEnd =
				session.finalsSinceStart === 0 && Date.now() - session.startedAt < RAPID_END_MS;
			session.rapidEnds = isRapidEnd ? session.rapidEnds + 1 : 0;
			if (session.rapidEnds >= MAX_RAPID_ENDS) {
				session.stopReason = session.segments.length === 0 ? 'start-error' : 'error';
				finish(session);
				return;
			}
			// The browser ends recognition on its own after a pause or an internal timeout. A
			// restart that throws (Safari, on an overlapping start) is ignored: the recogniser is
			// still running or fires `end` again.
			startRecognition(session);
		};

		setStatus('starting');
		if (!startRecognition(session)) {
			session.stopReason = 'start-error';
			finish(session);
		}
	}, [finish, stopAndWait]);

	const confirm = useCallback((): Promise<string> => {
		const session = sessionRef.current;
		if (!session || session.isStopping) {
			return Promise.resolve('');
		}
		return new Promise((resolve) => {
			session.resolveConfirm = resolve;
			stopAndWait(session);
		});
	}, [stopAndWait]);

	const cancel = useCallback(() => {
		const session = sessionRef.current;
		if (session) {
			finish(session, { discard: true, abort: true });
		}
	}, [finish]);

	// Only a live recogniser is aborted, so StrictMode's simulated unmount cannot end a dictation.
	useEffect(
		() => () => {
			const session = sessionRef.current;
			if (session) {
				finish(session, { discard: true, abort: true });
			}
		},
		[finish]
	);

	return { isSupported: getRecognitionConstructor() !== undefined, status, start, confirm, cancel };
};

export default useVoiceDictation;
